<template>
  <div ref="toneLibraryRef">
    <SectionTemplate
      title="400+真人级音色库 总有一种为您而生"
      desc="我们拥有覆盖全年龄段、多国语言与方言的庞大真人级音色库。无论是沉稳商务、亲切科普还是青春活泼，应有尽有。"
    >
      <div class="tone-card-grid">
        <ToneCard v-for="(item, index) in toneList" :key="index" :item="item" />
      </div>

      <ArrowBtn
        class="tone-more-btn"
        text="查看更多声音"
        variant="border-white-middle"
        @click="handleMore"
      />
    </SectionTemplate>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import ArrowBtn from '~/design/components/buttons/arrow-btn.vue';
import ToneCard, { type VoiceCardItem } from '~/design/components/tone-card/index.vue';
import SectionTemplate from '~/design/widgets/landing-section/cn-template.vue';
import { useAudioStore } from '~/shared/store/audio';

const handleReset = () => {
  const audioStore = useAudioStore();
  audioStore.stopAudio();
};

const handleMore = () => {
  const userInfo = useUserInfo();
  if (!userInfo.value) {
    navigateTo(
      `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace/voice`))}`,
    );
  } else {
    location.href = '/workspace';
  }
};

const toneLibraryRef = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

onMounted(() => {
  observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) {
        handleReset();
      }
    },
    { threshold: 0 },
  );
  if (toneLibraryRef.value) {
    observer.observe(toneLibraryRef.value);
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});

const toneList: VoiceCardItem[] = [
  {
    name: '热心学姐',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__26_pro.wav',
    id: 'XMOV_HN_TTS__26',
    gender: 'Female',
    subtitle: '青年分享 阳光开朗',
  },
  {
    name: '专业女声',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__8_pro.wav',
    id: 'XMOV_HN_TTS__8',
    gender: 'Female',
    subtitle: '青年讲解 专业干练',
  },
  {
    name: '谦和长者',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__36_pro.wav',
    id: 'XMOV_HN_TTS__36',
    gender: 'Male',
    subtitle: '老年讲解 深沉渊博',
  },
  {
    name: '故事姐姐',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__41_pro.wav',
    id: 'XMOV_HN_TTS__41',
    gender: 'Female',
    subtitle: '青年幼教 绘本阅读',
  },
  {
    name: '知心博主',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__12_pro.wav',
    id: 'XMOV_HN_TTS__12',
    gender: 'Female',
    subtitle: '中年博主 温柔暖心',
  },
  {
    name: '大气主持',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__21_pro.wav',
    id: 'XMOV_HN_TTS__21',
    gender: 'Female',
    subtitle: '中年主持 专业大气',
  },
  {
    name: '优雅主播',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_LV_TTS__13_pro.wav',
    id: 'XMOV_LV_TTS__13',
    gender: 'Female',
    subtitle: '青年主播 大方优雅',
  },
  {
    name: '贴心助手',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__43_pro.wav',
    id: 'XMOV_HN_TTS__43',
    gender: 'Female',
    subtitle: '青年助手 善解人意',
  },
  {
    name: '阳光男孩',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__18_pro.wav',
    id: 'XMOV_HN_TTS__18',
    gender: 'Male',
    subtitle: '青年种草 阳光开朗',
  },
  {
    name: '灵气女声',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__7_pro.wav',
    id: 'XMOV_HN_TTS__7',
    gender: 'Female',
    subtitle: '青年种草 可爱灵动',
  },
  {
    name: '爽朗男声',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__248_pro.wav',
    id: 'XMOV_HN_TTS__248',
    gender: 'Male',
    subtitle: '爽朗青年 自然对话',
  },
  {
    name: '亲和教师',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__33_pro.wav',
    id: 'XMOV_HN_TTS__33',
    gender: 'Female',
    subtitle: '青年幼教 亲切柔和',
  },
  {
    name: '清爽男大',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__249_pro.wav',
    id: 'XMOV_HN_TTS__249',
    gender: 'Male',
    subtitle: '温柔青年 暖心对话',
  },
  {
    name: '邻家女孩',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__19_pro.wav',
    id: 'XMOV_HN_TTS__19',
    gender: 'Female',
    subtitle: '青年对话 自然轻松',
  },
  {
    name: '温柔讲师',
    audioProUrl: 'https://media.youyan.xyz/youyan/TTS/XMOV_HN_TTS__27_pro.wav',
    id: 'XMOV_HN_TTS__27',
    gender: 'Female',
    subtitle: '青年讲师 温婉柔和',
  },
];
</script>

<style lang="scss" scoped>
.tone-card-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: 24px;
  column-gap: 16px;
}
.tone-more-btn {
  margin-top: 80px;
}
</style>
<style lang="scss" scoped src="./styles/mobile.scss"></style>
